'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useAuth } from '@/lib/auth';
import { MEETING_MANAGEMENT_ROLES } from '@/features/meeting-attendance/constants/roles';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';
import { COMMON_TIMEZONES, convertToUTC, TENANT_DEFAULT_TIMEZONE } from '@/features/meeting-attendance/utils/timezone';
import { ArrowLeft, Clock, FileText, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import CityAutocompleteInput from '@/components/common/CityAutocompleteInput';
import { Switch } from '@/components/ui/switch';

export default function MeetingAttendanceCreatePage() {
  const router = useRouter();
  const { t } = useTranslation();
  const { isReady } = useAuthGuard();
  const { hasAnyRole, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    location: '',
    city: '',
    enforceCheckinMode: false,
    type: 'OFFLINE',
    timezone: TENANT_DEFAULT_TIMEZONE,
  });

  const canManage = isAdmin || hasAnyRole(MEETING_MANAGEMENT_ROLES);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        startTime: formData.startTime
          ? convertToUTC(formData.startTime, formData.timezone).toISOString()
          : '',
        endTime: formData.endTime
          ? convertToUTC(formData.endTime, formData.timezone).toISOString()
          : '',
      };

      const meeting = await meetingAttendanceApi.createMeeting(submitData);
      router.push(`/meetingattendance/meetings/${meeting.id}`);
    } catch (error) {
      console.error('Failed to create meeting:', error);
      toast.error(t.meetingAttendance.messages.createFailed);
    } finally {
      setLoading(false);
    }
  };

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <p className="text-gray-600">{t.meetingAttendance.common.loading}</p>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t.meetingAttendance.permissions.noAccessTitle}
              </h3>
              <p className="text-gray-500 mb-4">
                {t.meetingAttendance.permissions.noAccessDescription}
              </p>
              <Link href="/meetingattendance/meetings">
                <Button variant="outline">{t.meetingAttendance.permissions.backToMeetings}</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center space-x-4">
        <Link href="/meetingattendance/meetings">
          <Button variant="outline" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t.meetingAttendance.common.back}
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t.meetingAttendance.meetingForm.createTitle}</h1>
          <p className="mt-1 text-sm text-gray-500">{t.meetingAttendance.meetingForm.createSubtitle}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t.meetingAttendance.meetingForm.infoTitle}</CardTitle>
          <CardDescription>{t.meetingAttendance.meetingForm.infoDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                <FileText className="inline mr-1 h-4 w-4" />
                {t.meetingAttendance.meetingForm.titleLabel} *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t.meetingAttendance.meetingForm.titlePlaceholder}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                {t.meetingAttendance.meetingForm.descriptionLabel}
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t.meetingAttendance.meetingForm.descriptionPlaceholder}
              />
            </div>

            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
                {t.meetingAttendance.meetingForm.timezoneLabel} *
              </label>
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="inline mr-1 h-4 w-4" />
                  {t.meetingAttendance.meetingForm.startTimeLabel} *
                </label>
                <input
                  type="datetime-local"
                  id="startTime"
                  name="startTime"
                  required
                  value={formData.startTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="inline mr-1 h-4 w-4" />
                  {t.meetingAttendance.meetingForm.endTimeLabel} *
                </label>
                <input
                  type="datetime-local"
                  id="endTime"
                  name="endTime"
                  required
                  value={formData.endTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="inline mr-1 h-4 w-4" />
                {t.meetingAttendance.meetingForm.locationLabel}
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t.meetingAttendance.meetingForm.locationPlaceholder}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  {t.meetingAttendance.meetingForm.cityLabel}
                </label>
                <CityAutocompleteInput
                  id="city"
                  value={formData.city}
                  onChange={(v) => setFormData({ ...formData, city: v })}
                  placeholder={t.meetingAttendance.meetingForm.cityPlaceholder}
                />
                <p className="text-xs text-gray-500 mt-1">{t.meetingAttendance.meetingForm.cityHint}</p>
              </div>
              <div>
                <label htmlFor="enforceCheckinMode" className="block text-sm font-medium text-gray-700 mb-1">
                  {t.meetingAttendance.enforceCheckinMode.label}
                </label>
                <div className="flex items-center gap-3 mt-2">
                  <Switch
                    id="enforceCheckinMode"
                    checked={formData.enforceCheckinMode}
                    disabled={!formData.enforceCheckinMode && !formData.city.trim()}
                    onCheckedChange={(next) => setFormData({ ...formData, enforceCheckinMode: next })}
                  />
                  <span className={`text-sm ${formData.enforceCheckinMode ? 'text-emerald-600' : 'text-gray-500'}`}>
                    {formData.enforceCheckinMode
                      ? t.meetingAttendance.enforceCheckinMode.on
                      : t.meetingAttendance.enforceCheckinMode.off}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{t.meetingAttendance.meetingForm.enforceCheckinModeHint}</p>
              </div>
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                {t.meetingAttendance.meetingForm.typeLabel} *
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="OFFLINE">{t.meetingAttendance.meetingForm.typeOffline}</option>
                <option value="ONLINE">{t.meetingAttendance.meetingForm.typeOnline}</option>
                <option value="HYBRID">{t.meetingAttendance.meetingForm.typeHybrid}</option>
              </select>
            </div>

            <div className="flex justify-end space-x-4">
              <Link href="/meetingattendance/meetings">
                <Button type="button" variant="outline">
                  {t.meetingAttendance.meetingForm.cancel}
                </Button>
              </Link>
              <Button type="submit" disabled={loading}>
                {loading ? t.meetingAttendance.meetingForm.saving : t.meetingAttendance.meetingForm.createAction}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
